import React, { useEffect, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import MDBox from "components/MDBox";
import { Box, Button, IconButton, Typography, Chip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Card, CardContent, CardHeader, Divider } from "@mui/material";
import CostosCliente from "./CostosCliente";
import PropTypes from "prop-types";

import TableMaestros from "verticales/TableMaestros.tsx";
import UtilModal from "verticales/UtilModalDisponibilidad.js";
import AppLoading from "verticales/AppLoading.tsx";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import StorageIcon from "@mui/icons-material/Storage";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Tooltip from "@mui/material/Tooltip";

// Componente de tarjeta de estadística
const StatCard = ({ icon: Icon, label, value, color = "primary" }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${
        color === "primary"
          ? "#edf5f1 0%, #137af0 100%"
          : color === "success"
          ? "#e5f1f0 0%, #137af0 100%"
          : "#e5f1f0 0%, #137af0 100%"
      })`,
      color: "white",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
      },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Icon sx={{ fontSize: 32, opacity: 0.9 }} />
        <Typography
          variant="caption"
          sx={{ opacity: 0.85, textTransform: "uppercase", letterSpacing: 1 }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.oneOf(["primary", "success", "error"]),
};

function ViewGraphicCost(props) {
  const [blocking, setBlocking] = useState(true);

  const tableData = useMemo(
    () => (Array.isArray(props?.data?.rows) ? props.data.rows : []),
    [props?.data]
  );

  useEffect(() => {
    setBlocking(!Array.isArray(props?.data?.rows));
  }, [props?.data]);

  // Calcular estadísticas resumidas
  const stats = useMemo(() => {
    if (!tableData.length) return { total: 0, databases: 0, avgCost: 0 };

    const total = tableData.reduce(
      (sum, row) => sum + (parseFloat(row.costo_total_cliente) || 0),
      0
    );
    const databases = tableData.length;
    const avgCost = databases > 0 ? total / databases : 0;

    return { total, databases, avgCost };
  }, [tableData]);

  const columns = useMemo(
    () => [
      {
        header: "RDS ID",
        accessorKey: "rds_id",
        cell: (info) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StorageIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body2" fontWeight={500}>
              {info.getValue()}
            </Typography>
          </Box>
        ),
      },
      {
        header: "Base de Datos",
        accessorKey: "database_name",
        cell: (info) => (
          <Typography variant="body2" color="text.secondary">
            {info.getValue()}
          </Typography>
        ),
      },
      {
        header: "Costo Total",
        accessorKey: "costo_total_cliente",
        cell: (info) => (
          <Chip
            icon={<AttachMoneyIcon sx={{ fontSize: 20 }} />}
            label={`$${parseFloat(info.getValue() || 0).toLocaleString("es-CO", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
            color="success"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
    ],
    []
  );

  const [rowSelect, setRowSelect] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = (row) => {
    setRowSelect(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const renderView = () => {
    return <CostosCliente row={rowSelect} handleCloseModal={handleCloseModal} />;
  };

  const renderOpcRowsSid = (row) => (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", p: 0 }}>
      <Tooltip title="Ver detalles de costos" arrow placement="left">
        <IconButton
          onClick={() => handleOpenModal(row)}
          sx={{
            color: "#137af0",
            "&:hover": {
              backgroundColor: "primary.light",
              color: "white",
            },
            transition: "all 0.3s ease",
          }}
        >
          <AnalyticsIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <>
      <MDBox py={2}>
        {blocking ? (
          <Card
            sx={{ minHeight: 400, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <AppLoading titleCustom={"costos-siesa"} />
          </Card>
        ) : (
          <Grid container spacing={3}>
            {/* Tarjetas de estadísticas */}
            <Grid item xs={12} md={4}>
              <StatCard
                icon={AttachMoneyIcon}
                label="Costo Total"
                value={`$${stats.total.toLocaleString("es-CO", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={StorageIcon}
                label="Total Bases de Datos"
                value={stats.databases}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={AnalyticsIcon}
                label="Costo Promedio Cliente"
                value={`$${stats.avgCost.toLocaleString("es-CO", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                color="success"
              />
            </Grid>

            {/* Tabla de datos */}
            <Grid item xs={12}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  transition: "box-shadow 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardHeader
                  title={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <StorageIcon color="#137af0" />
                      <Typography variant="h6" fontWeight={600}>
                        Detalle de Costos por Base de Datos
                      </Typography>
                    </Box>
                  }
                  subheader={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Visualiza y analiza los costos de cada base de datos
                    </Typography>
                  }
                  sx={{
                    pb: 2,
                    backgroundColor: "grey.50",
                  }}
                />
                <Divider />
                <CardContent sx={{ p: 0 }}>
                  <MDBox m={2}>
                    <TableMaestros
                      arrayColumns={columns}
                      data={tableData}
                      handleOpenModal={handleOpenModal}
                      height={350}
                      renderOpcRows={renderOpcRowsSid}
                    />
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <UtilModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        renderView={renderView}
      />
    </>
  );
}

ViewGraphicCost.propTypes = {
  data: PropTypes.shape({
    rows: PropTypes.array,
  }),
};

export default ViewGraphicCost;
